import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getUnitById } from '@/api/units.api'
import type { Unit } from '@/types/api'
import { formatDateTime } from '@/utils/format'

interface UnitDetailDrawerProps {
  unitId: string | null
  onClose: () => void
  onUpdate?: (unit: Unit) => void
}

export function UnitDetailDrawer({
  unitId,
  onClose,
  onUpdate,
}: UnitDetailDrawerProps) {
  const { data: unit, isLoading } = useQuery({
    queryKey: ['unit-detail', unitId],
    queryFn: () => (unitId ? getUnitById(unitId) : Promise.resolve(null)),
    enabled: !!unitId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const unitData = unit as any

  return (
    <Drawer
      open={!!unitId}
      onClose={onClose}
      title="CHI TIẾT ĐƠN VỊ"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => unit && onUpdate?.(unit)}
            className="min-w-[160px] h-10"
            disabled={!unit || isLoading}
          >
            Cập nhật
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {unitData && (
        <div className="space-y-6">
          {/* 1. Thông tin chung Section */}
          <DrawerSection title="Thông tin chung">
            <DrawerDetailRow label="Tên đơn vị:" value={unitData.name} />
            <DrawerDetailRow label="Mã đơn vị:" value={unitData.code} />
            <DrawerDetailRow
              label="Cấp:"
              value={unitData.level || 'Cấp Tỉnh'}
            />
            <DrawerDetailRow
              label="Số điện thoại:"
              value={unitData.phoneNumber || '--'}
            />
            <DrawerDetailRow
              label="Ngày bắt đầu:"
              value={
                unitData.startDate
                  ? formatDateTime(unitData.startDate, 'DD/MM/YYYY')
                  : '--'
              }
            />
            <DrawerDetailRow
              label="Ngày kết thúc:"
              value={
                unitData.endDate
                  ? formatDateTime(unitData.endDate, 'DD/MM/YYYY')
                  : '--'
              }
            />
            <DrawerDetailRow
              label="Trạng thái:"
              value={
                <span
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                    unitData.status === 1
                      ? 'bg-[#EAF7ED] text-[#2FB14F]'
                      : 'bg-[#FDECEB] text-[#EE4036]'
                  }`}
                >
                  {unitData.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                </span>
              }
            />
            <DrawerDetailRow
              label="Ghi chú:"
              value={unitData.description || '--'}
              alignTop
            />
          </DrawerSection>

          <DrawerSection title="Người Đại Diện">
            <DrawerDetailRow
              label="Người đại diện:"
              value={unitData.fullNameAdmin || '--'}
            />
            <DrawerDetailRow
              label="Số điện thoại người đại diện:"
              value={unitData.phoneNumberAdmin || '--'}
            />
            <DrawerDetailRow
              label="Email người đại diện:"
              value={unitData.personalEmailAdmin || '--'}
            />
          </DrawerSection>

          <DrawerSection title="Thông tin tài khoản">
            <DrawerDetailRow
              label="Email:"
              value={unitData.emailAdmin || '--'}
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}
